import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, RotateCcw, Send, Trash2, X } from 'lucide-react';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Skeleton } from '../../../shared/components/Skeleton';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import type { FeatureKey } from '../../../shared/lib/permissions';
import { useCancelarRequisicao, useDecidirRequisicao, useEnviarRequisicao, useRequisicao } from '../hooks';
import { DecisaoModal, type DecisaoTipo } from '../components/DecisaoModal';
import { MotivoModal } from '../components/MotivoModal';
import { TrilhaAprovacao } from '../components/TrilhaAprovacao';
import { DevolucoesBadge, RequisicaoStatusBadge, VagaStatusBadge } from '../components/RecrutamentoBadges';
import {
  APPROVAL_STEP_LABELS,
  JOB_TYPE_LABELS,
  REQUISITION_EVENT_LABELS,
  formatClienteLabel,
  formatCurrencyBRL,
  formatDateTimeBR,
} from '../components/format';

/** Feature RBAC que autoriza decidir cada passo da cadeia. */
const STEP_FEATURES: Record<string, FeatureKey> = {
  gestor: FEATURES.recrutamentoAprovacaoGestor,
  rh: FEATURES.recrutamentoAprovacaoRh,
  financeiro: FEATURES.recrutamentoAprovacaoFinanceiro,
  diretoria: FEATURES.recrutamentoAprovacaoDiretoria,
  recrutamento: FEATURES.recrutamentoAprovacaoRecrutamento,
};

/**
 * Detalhe da requisição: dados, trilha de aprovação, histórico imutável
 * e as ações disponíveis.
 *
 * As ações de decisão só aparecem quando **este** usuário pode decidir o
 * passo corrente — o botão é derivado do passo pendente cruzado com a
 * feature da sessão, não do perfil por nome.
 */
export default function RequisicaoDetalhePage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data, isLoading, isError } = useRequisicao(id);

  const decidir = useDecidirRequisicao();
  const enviar = useEnviarRequisicao();
  const cancelar = useCancelarRequisicao();

  const [decisao, setDecisao] = useState<DecisaoTipo | null>(null);
  const [confirmarCancelamento, setConfirmarCancelamento] = useState(false);

  const passoCorrente = useMemo(() => {
    if (!data) return null;
    return (
      data.approvals
        .filter((step) => step.cycle === data.approvalCycle && step.decision === 'pendente')
        .sort((a, b) => a.sortOrder - b.sortOrder)[0] ?? null
    );
  }, [data]);

  const podeDecidir =
    passoCorrente !== null &&
    data?.status === 'em_aprovacao' &&
    can(STEP_FEATURES[passoCorrente.stepKey] ?? FEATURES.recrutamentoRequisicoes, 'edit');

  const podeEnviar = data?.status === 'rascunho' || data?.status === 'devolvida';
  const podeCancelar =
    data !== undefined &&
    ['rascunho', 'em_aprovacao', 'devolvida'].includes(data.status) &&
    can(FEATURES.recrutamentoRequisicoes, 'delete');

  if (isLoading) {
    return (
      <PageContainer title="Requisição de vaga">
        <Skeleton className="h-40 w-full" />
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer title="Requisição de vaga">
        <p role="alert" className="text-sm text-red-600">
          Não foi possível carregar a requisição.
        </p>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={`${data.code} — ${data.positionTitle}`}
      subtitle={`${data.requestingArea} · ${data.vacanciesCount} ${data.vacanciesCount === 1 ? 'vaga' : 'vagas'}`}
      breadcrumbs={[
        { label: 'Requisições', to: '/recrutamento/requisicoes' },
        { label: data.code },
      ]}
      action={
        <div className="flex flex-wrap items-center gap-2">
          {podeEnviar && (
            <Button
              icon={<Send className="h-4 w-4" />}
              loading={enviar.isPending}
              onClick={() => enviar.mutate(data.id)}
            >
              {data.status === 'devolvida' ? 'Reenviar' : 'Enviar para aprovação'}
            </Button>
          )}
          {podeDecidir && (
            <>
              <Button icon={<Check className="h-4 w-4" />} onClick={() => setDecisao('aprovar')}>
                Aprovar
              </Button>
              <Button
                variant="secondary"
                icon={<RotateCcw className="h-4 w-4" />}
                onClick={() => setDecisao('devolver')}
              >
                Devolver
              </Button>
              <Button
                variant="danger"
                icon={<X className="h-4 w-4" />}
                onClick={() => setDecisao('reprovar')}
              >
                Reprovar
              </Button>
            </>
          )}
          {podeCancelar && (
            <Button
              variant="ghost"
              icon={<Trash2 className="h-4 w-4" />}
              onClick={() => setConfirmarCancelamento(true)}
            >
              Cancelar
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <RequisicaoStatusBadge status={data.status} />
        <DevolucoesBadge count={data.returnCount} />
        {data.jobOpening && <VagaStatusBadge status={data.jobOpening.status} />}
        {passoCorrente && data.status === 'em_aprovacao' && (
          <span className="text-xs text-gray-500">
            Aguardando {APPROVAL_STEP_LABELS[passoCorrente.stepKey] ?? passoCorrente.stepKey}
          </span>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="space-y-4 lg:col-span-2">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Dados da solicitação</h2>
            <dl className="grid gap-3 sm:grid-cols-2">
              <Campo label="Empresa" value={data.company?.tradeName ?? data.company?.corporateName} />
              <Campo
                label="Cliente"
                value={data.client ? formatClienteLabel(data.client) : undefined}
              />
              <Campo
                label="Centro de custo"
                value={
                  data.costCenter ? `${data.costCenter.code} — ${data.costCenter.name}` : undefined
                }
              />
              <Campo label="Tipo da vaga" value={JOB_TYPE_LABELS[data.jobType]} />
              <Campo label="Motivo" value={data.hiringReason?.label} />
              {data.replacedEmployeeName && (
                <Campo label="Colaborador substituído" value={data.replacedEmployeeName} />
              )}
              <Campo label="Salário previsto" value={formatCurrencyBRL(data.expectedSalary)} />
            </dl>

            <div className="mt-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Benefícios</p>
              {data.benefits && data.benefits.length > 0 ? (
                <ul className="mt-1 list-inside list-disc text-sm text-gray-700">
                  {data.benefits.map((benefit) => (
                    <li key={benefit.id ?? benefit.description}>
                      {benefit.description}
                      {benefit.estimatedValue
                        ? ` — ${formatCurrencyBRL(benefit.estimatedValue)}`
                        : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-gray-500">Nenhum benefício informado.</p>
              )}
            </div>

            <div className="mt-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Justificativa</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{data.justification}</p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Histórico</h2>
            <ol className="space-y-2">
              {data.history.map((entry) => (
                <li key={entry.id} className="text-sm">
                  <span className="font-medium text-gray-900">
                    {REQUISITION_EVENT_LABELS[entry.event] ?? entry.event}
                  </span>
                  {entry.stepKey && (
                    <span className="text-gray-500">
                      {' '}
                      — {APPROVAL_STEP_LABELS[entry.stepKey] ?? entry.stepKey}
                    </span>
                  )}
                  <span className="text-gray-400"> · {formatDateTimeBR(entry.createdAt)}</span>
                  {entry.parecer && (
                    <p className="mt-0.5 whitespace-pre-wrap text-gray-600">{entry.parecer}</p>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">Cadeia de aprovação</h2>
          <TrilhaAprovacao approvals={data.approvals} currentCycle={data.approvalCycle} />
        </section>
      </div>

      {passoCorrente && (
        <DecisaoModal
          open={decisao !== null}
          tipo={decisao ?? 'aprovar'}
          stepKey={passoCorrente.stepKey}
          code={data.code}
          loading={decidir.isPending}
          error={(decidir.error as Error | null)?.message ?? null}
          onCancel={() => setDecisao(null)}
          onConfirm={(parecer) =>
            decidir.mutate(
              {
                id: data.id,
                decision: decisao ?? 'aprovar',
                // Manda o passo que a tela viu: se a cadeia andou nesse
                // meio-tempo, o servidor responde 409 em vez de decidir
                // um passo que o usuário não estava olhando.
                stepKey: passoCorrente.stepKey,
                parecer,
              },
              { onSuccess: () => setDecisao(null) },
            )
          }
        />
      )}

      <MotivoModal
        open={confirmarCancelamento}
        title="Cancelar requisição"
        message="A requisição é encerrada e os aprovadores envolvidos são avisados."
        confirmLabel="Cancelar requisição"
        danger
        loading={cancelar.isPending}
        error={(cancelar.error as Error | null)?.message ?? null}
        onCancel={() => setConfirmarCancelamento(false)}
        onConfirm={(reason) =>
          cancelar.mutate(
            { id: data.id, reason },
            {
              onSuccess: () => {
                setConfirmarCancelamento(false);
                navigate('/recrutamento/requisicoes');
              },
            },
          )
        }
      />
    </PageContainer>
  );
}

function Campo({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value ?? '—'}</dd>
    </div>
  );
}
